import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  SquarePen,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Input } from "../ui/input";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { ConfirmationDialog } from "@/widgets/AlertCommon";
import { useBreadcrumbs } from "../BreadcrumbContext";
import {
  useInterfaceRepository,
  deleteInterfaceData,
  toggleInterfaceStatus,
} from "@/hooks/useLocalStorage";
import { InterfaceAction } from "@/lib/api-services";
import { AddInterfaceData } from "./AddInterfaceData";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

interface TableInterfaceProps {
  topLevelData: InterfaceAction[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onEditInterface: (data: InterfaceAction) => void;
}

function TableInterface({
  topLevelData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
  onEditInterface,
}: TableInterfaceProps) {
  const [currentData, setCurrentData] = useState<InterfaceAction[]>([]);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InterfaceAction | null>(null);
  const [itemToToggle, setItemToToggle] = useState<InterfaceAction | null>(null);

  const deleteMutation = deleteInterfaceData();
  const toggleMutation = toggleInterfaceStatus();

  React.useEffect(() => {
    if (topLevelData) {
      setCurrentData(topLevelData);
    }
  }, [topLevelData]);

  const handleDeleteClick = (e: React.MouseEvent, item: InterfaceAction) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!itemToDelete) return;
    deleteMutation.mutate(itemToDelete.Id);
  };

  const handleStatusToggle = (e: React.MouseEvent, item: InterfaceAction) => {
    e.stopPropagation();
    setItemToToggle(item);
    setStatusDialogOpen(true);
  };

  const handleConfirmStatusToggle = () => {
    if (!itemToToggle) return;
    toggleMutation.mutate(itemToToggle.Id);
  };

  React.useEffect(() => {
    if (deleteMutation.isSuccess) {
      setDeleteDialogOpen(false);
      setReqStatus(true);
      setAlertMessage("Interface deleted successfully!");
      setItemToDelete(null);
      setAlertOpen(true);
      deleteMutation.reset();
    } else if (deleteMutation.isError) {
      const raw = deleteMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(false);
      setDeleteDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      deleteMutation.reset();
    }
  }, [deleteMutation]);

  React.useEffect(() => {
    if (toggleMutation.isSuccess) {
      setStatusDialogOpen(false);
      setReqStatus(true);
      setAlertMessage(
        itemToToggle?.implementStatus === true
          ? "Interface deactivated successfully!"
          : "Interface activated successfully!"
      );
      setItemToToggle(null);
      setAlertOpen(true);
      toggleMutation.reset();
    } else if (toggleMutation.isError) {
      const raw = toggleMutation.error as Error;
      const parsedMsg = parseApiErrorMessage(raw.message);
      setReqStatus(false);
      setStatusDialogOpen(false);
      setAlertMessage(parsedMsg);
      setAlertOpen(true);
      toggleMutation.reset();
    }
  }, [toggleMutation]);

  const getTypeLabel = (type: number) => {
    return type === 0 ? "Internal" : "External";
  };

  const getStatusLabel = (useFlag: boolean) => {
    return useFlag ? "Active" : "Inactive";
  };

  const getImplementationLabel = (implementStatus: boolean) => {
    return implementStatus ? "Implemented" : "Not Implemented";
  };

  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Input</TableHead>
              <TableHead>Output</TableHead>
              <TableHead>Implementation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.Id}>
                <TableCell className="">{data.Id}</TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.name}
                </TableCell>
                <TableCell>{getTypeLabel(data.type)}</TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.description}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.input}
                </TableCell>
                <TableCell className="max-w-xs truncate">
                  {data.output}
                </TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${data.implementStatus
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                      }`}
                  >
                    {getImplementationLabel(data.implementStatus)}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${data.status
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                      }`}
                  >
                    {getStatusLabel(data.status)}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-row items-center space-x-2">
                    <button
                      onClick={() => onEditInterface(data)}
                      title="Edit Interface"
                      className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                    >
                      <SquarePen size={20} strokeWidth={2.5} />
                    </button>

                    <button
                      onClick={(e) => handleStatusToggle(e, data)}
                      title={data.status ? "Deactivate" : "Activate"}
                      className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                    >
                      {data.status ? (
                        <X size={20} strokeWidth={2.5} className="text-red-800" />
                      ) : (
                        <Check size={20} strokeWidth={2.5} className="text-green-800" />
                      )}
                    </button>

                    <button
                      onClick={(e) => handleDeleteClick(e, data)}
                      title="Delete Interface"
                      className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100 text-red-500"
                    >
                      <Trash2 size={20} strokeWidth={2.5} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        isPending={deleteMutation.isPending}
        itemName={itemToDelete as InterfaceAction}
        itemType={"Interface"}
        requireAuthCode={false}
      />

      <ConfirmationDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        onConfirm={handleConfirmStatusToggle}
        isPending={toggleMutation.isPending}
        heading={itemToToggle?.status === true ? "Confirm Deactivation" : "Confirm Activation"}
        message={`Are you sure you want to ${itemToToggle?.status === true ? "deactivate" : "activate"
          } this interface?`}
        confirmLabel={itemToToggle?.status === true ? "Deactivate" : "Activate"}
      />
    </div>
  );
}

interface InterfacesRepoProps {
  fromType?: string;
  formRoute?: string;
}

export function InterfacesRepo({ fromType = "interface", formRoute = "interfaces" }: InterfacesRepoProps) {
  const [, setLocation] = useLocation();
  const { setBreadcrumbs } = useBreadcrumbs();

  const [intId, setIntId] = useState<string>("");
  const [intName, setIntName] = useState<string>("");
  const [intType, setIntType] = useState<string>("");
  const [filterOpen, setFilterOpen] = useState(false);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [editInterfaceData, setEditInterfaceData] = useState<InterfaceAction | null>(null);

  const [appliedIntId, setAppliedIntId] = useState<string>("");
  const [appliedIntName, setAppliedIntName] = useState<string>("");
  const [appliedIntType, setAppliedIntType] = useState<string>("");

  const { data: interfaceData, isLoading } = useInterfaceRepository(
    pageIndex,
    pageSize,
    appliedIntId,
    appliedIntName,
    appliedIntType
  );

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: "Interfaces Repository",
        path: `/${formRoute}`,
      },
    ]);
  }, [formRoute, setBreadcrumbs]);

  React.useEffect(() => {
    setPageIndex(1);
  }, [fromType]);

  const handleSearch = () => {
    if (intId) setAppliedIntId(JSON.parse(intId));
    setAppliedIntName(intName);
    setAppliedIntType(intType);
    setPageIndex(1);
  };

  const handleRefresh = (close: boolean) => {
    setIntId("");
    setIntName("");
    setIntType("");
    setAppliedIntId("");
    setAppliedIntName("");
    setAppliedIntType("");
    setPageIndex(1);
    if (close) setFilterOpen(false);
  };

  const handleAddInterface = () => {
    setEditInterfaceData(null);
    setOpenCreateDialog(true);
  };

  const handleEditInterface = (data: InterfaceAction) => {
    setEditInterfaceData(data);
    setOpenCreateDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenCreateDialog(false);
    setEditInterfaceData(null);
  };

  return (
    <div className="min-h-full">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-black">
              Manage Interfaces
            </h2>
            <p className="text-black mt-1 text-sm md:text-base">
              Create and Manage Internal and External Interfaces
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen(true)}
            >
              Filter Interface
            </Button>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={handleAddInterface}
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
              Add Interface
            </Button>
          </div>
        </div>
      </div>

      {filterOpen && (
        <Card className="mb-4 md:mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-end md:space-x-4 space-y-3 md:space-y-0">
              <div className="flex-1">
                <Label className="text-sm font-medium mb-1.5 block">Interface ID</Label>
                <Input
                  type="number"
                  placeholder="Enter Interface ID"
                  value={intId}
                  onChange={(e) => setIntId(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm font-medium mb-1.5 block">Interface Name</Label>
                <Input
                  type="text"
                  placeholder="Enter Interface Name"
                  value={intName}
                  onChange={(e) => setIntName(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-sm font-medium mb-1.5 block">Interface Type</Label>
                <Select value={intType} onValueChange={setIntType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Internal</SelectItem>
                    <SelectItem value="1">External</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex justify-center space-x-2">
              <Button onClick={handleSearch}>Search</Button>
              <Button variant="outline" onClick={() => handleRefresh(false)}>
                Refresh
              </Button>
              <Button variant="outline" onClick={() => handleRefresh(true)}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading ? (
        <Card>
          <CardContent className="p-4 md:p-6">
            <div className="space-y-2">
              {interfaceData?.items && interfaceData.items.length > 0 ? (
                <TableInterface
                  topLevelData={interfaceData.items}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={interfaceData.total || 0}
                  onPageChange={setPageIndex}
                  onEditInterface={handleEditInterface}
                />
              ) : (
                <div className="text-center py-8 text-black">
                  <p>No interfaces found. Create one to get started.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="p-6 text-center text-black">Loading interfaces...</div>
      )}


      <AddInterfaceData
        open={openCreateDialog}
        onClose={handleCloseDialog}
        editData={editInterfaceData}
      />
    </div>
  );
}